import { Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { formatInr } from "../../shared/utils/currency";

export type ResourceConfig = {
  title: string;
  subtitle: string;
  listUrl: string;
  listMethod?: "GET" | "POST";
  createModule?: string;
  deleteUrl?: string;
  canEditRow?: (row: any) => boolean;
  canDeleteRow?: (row: any) => boolean;
  columns: ColumnsType<any>;
};

export const resourceConfigs: Record<string, ResourceConfig> = {
  party: {
    title: "Party",
    subtitle: "Party master using the same PartyCompositeForm from every launch point.",
    listUrl: "/parties",
    createModule: "party-composite",
    columns: [
      {
        title: "Party",
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Space wrap size={6}>
              <Tag color={partyTagColor(row.type)}>{row.type ?? "Party"}</Tag>
              <Typography.Text strong>{row.name}</Typography.Text>
              {row.code ? <Typography.Text type="secondary">#{row.code}</Typography.Text> : null}
            </Space>
            <Typography.Text type="secondary">
              {[row.phone, row.email].filter(Boolean).join(" | ") || row.city || "No contact details"}
            </Typography.Text>
          </Space>
        )
      },
      {
        title: "Details",
        width: 260,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text type="secondary">Voucher: <Typography.Text>{row.voucherType ?? "-"}</Typography.Text></Typography.Text>
            <Typography.Text type="secondary">Debit: <Typography.Text strong>{formatInr(row.debitTotal ?? 0)}</Typography.Text></Typography.Text>
            <Typography.Text type="secondary">Credit: <Typography.Text strong>{formatInr(row.creditTotal ?? 0)}</Typography.Text></Typography.Text>
          </Space>
        )
      }
    ]
  },
  bank: {
    title: "Bank",
    subtitle: "Bank and bank account setup with fixed shared forms.",
    listUrl: "/banks",
    createModule: "bank",
    columns: [
      { title: "Bank", dataIndex: "name" },
      { title: "IFSC", dataIndex: "ifsc", width: 160 },
      { title: "Accounts", dataIndex: "accountCount", width: 110 },
      { title: "Active", dataIndex: "activeAccountCount", width: 110 }
    ]
  },
  "short-remarks": {
    title: "Short Remarks",
    subtitle: "Reusable quick remark master used by entry screens and bulk grids.",
    listUrl: "/short-remarks",
    createModule: "short-remarks",
    deleteUrl: "/short-remarks",
    columns: [
      { title: "Remark", dataIndex: "name" },
      { title: "Active", dataIndex: "isActive", width: 110, render: (v) => <Tag color={v === false ? "red" : "green"}>{v === false ? "No" : "Yes"}</Tag> }
    ]
  },
  "sac-codes": {
    title: "SAC Codes",
    subtitle: "Tax code setup migrated as a shared master table surface.",
    listUrl: "/sac-codes/all",
    createModule: "sac-code",
    deleteUrl: "/sac-codes/delete",
    columns: [
      {
        title: "SAC Code",
        dataIndex: "sacCode",
        width: 180,
        render: (value, row) => <Space><Typography.Text strong>{value}</Typography.Text>{row.isActive === false ? <Tag color="orange">Inactive</Tag> : null}</Space>
      },
      { title: "CGST", dataIndex: "cgstRate", width: 120 },
      { title: "SGST", dataIndex: "sgstRate", width: 120 },
      { title: "IGST", dataIndex: "igstRate", width: 120 }
    ]
  },
  "financial-years": {
    title: "Financial Years",
    subtitle: "Financial year setup and locking surface.",
    listUrl: "/financial-years/all",
    createModule: "financial-year",
    deleteUrl: "/financial-years/delete",
    columns: [
      { title: "Code", dataIndex: "code", width: 130 },
      { title: "Start", dataIndex: "startDate", width: 160 },
      { title: "End", dataIndex: "endDate", width: 160 },
      { title: "Closed", dataIndex: "isClosed", width: 110, render: (v) => <Tag color={v ? "red" : "green"}>{v ? "Closed" : "Open"}</Tag> }
    ]
  },
  ledgers: {
    title: "Ledgers",
    subtitle: "Chart of accounts hierarchy/list surface with shared ledger forms.",
    listUrl: "/ledgers/all",
    createModule: "account-ledger",
    canEditRow: (row) => !row.isProtected && !row.isSystemLedger,
    canDeleteRow: (row) => !row.isProtected && !row.isSystemLedger,
    columns: [
      {
        title: "Ledger",
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Space wrap size={6}>
              <Typography.Text strong>{row.name}</Typography.Text>
              {row.isSystemLedger ? <Tag>System</Tag> : null}
              {row.isProtected ? <Tag color="red">Protected</Tag> : null}
              {row.isActive === false ? <Tag color="orange">Inactive</Tag> : null}
            </Space>
            <Typography.Text type="secondary">{row.parentName ? `Under: ${row.parentName}` : "Root group"}</Typography.Text>
          </Space>
        )
      },
      { title: "Type", dataIndex: "accountType", width: 140, render: (v) => <Tag color={ledgerTypeColor(v)}>{v}</Tag> },
      { title: "Group", dataIndex: "accountGroup", width: 220, render: (v) => v || "-" },
      {
        title: "Balance",
        width: 160,
        align: "right",
        render: (_, row) => {
          const balance = Number(row.balance ?? row.closingBalance ?? 0);
          const type = row.balanceType ?? (balance >= 0 ? "Dr" : "Cr");
          return (
            <Typography.Text className={balance >= 0 ? "ledger-balance-dr" : "ledger-balance-cr"} strong>
              {formatInr(Math.abs(balance))} {type}
            </Typography.Text>
          );
        }
      }
    ]
  },
  "tax-setup": {
    title: "Tax Ledger Setup",
    subtitle: "Posting profile and tax setup route preserved for the React shell.",
    listUrl: "/org-tax-setup",
    createModule: "tax-ledger-setup",
    columns: [
      { title: "Name", dataIndex: "name" },
      { title: "Tax Type", dataIndex: "taxType", width: 160 },
      { title: "Ledger", dataIndex: "ledgerName", width: 220 }
    ]
  },
  organizations: {
    title: "Manage Company",
    subtitle: "Organization management and assignment surface.",
    listUrl: "/organizations",
    createModule: "organization",
    columns: [
      {
        title: "Details",
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Space wrap size={6}>
              <Typography.Text strong>{row.name}</Typography.Text>
              {row.code ? <Typography.Text type="secondary">Code: {row.code}</Typography.Text> : null}
            </Space>
            <Typography.Text type="secondary">GST: {row.gstNumber ?? "-"} | PAN: {row.pan ?? "-"}</Typography.Text>
          </Space>
        )
      },
      {
        title: "Address",
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text>{[row.city, row.state].filter(Boolean).join(", ") || "-"}</Typography.Text>
            <Typography.Text type="secondary">{row.addressLine1 ?? ""}</Typography.Text>
          </Space>
        )
      }
    ]
  },
  users: {
    title: "Users",
    subtitle: "User management list. Role/permission editor will use the same drawer shell.",
    listUrl: "/users",
    createModule: "user",
    deleteUrl: "/users",
    columns: [
      { title: "Name", dataIndex: "fullName" },
      { title: "Email", dataIndex: "email" },
      { title: "Roles", dataIndex: "roles", render: (roles: string[] = []) => roles.map((r) => <Tag key={r}>{r}</Tag>) },
      { title: "Locked", dataIndex: "isLocked", width: 110, render: (v) => <Tag color={v ? "red" : "green"}>{v ? "Yes" : "No"}</Tag> }
    ]
  },
  "line-items": {
    title: "Line Item Templates",
    subtitle: "Reusable bill grid input columns.",
    listUrl: "/line-items/all",
    createModule: "line-item-template",
    deleteUrl: "/line-items/delete",
    columns: [
      { title: "Code", dataIndex: "code", width: 160 },
      { title: "Label", dataIndex: "label" },
      { title: "Unit", dataIndex: "unit", width: 120 },
      { title: "Required", dataIndex: "isRequired", width: 110, render: (v) => <Tag color={v ? "blue" : "default"}>{v ? "Yes" : "No"}</Tag> }
    ]
  },
  "bill-units": {
    title: "Bill Units",
    subtitle: "Unit definitions for invoice line items.",
    listUrl: "/bill-unit-definitions",
    createModule: "bill-unit",
    deleteUrl: "/bill-unit-definitions",
    columns: [
      { title: "Code", dataIndex: "code", width: 160 },
      { title: "Label", dataIndex: "label" },
      { title: "Order", dataIndex: "displayOrder", width: 110 },
      { title: "Active", dataIndex: "isActive", width: 110, render: (v) => <Tag color={v ? "green" : "red"}>{v ? "Yes" : "No"}</Tag> }
    ]
  }
};

function partyTagColor(type?: string) {
  if (type === "Vendor") return "blue";
  if (type === "Employee") return "green";
  if (type === "Customer") return "cyan";
  return "default";
}

function ledgerTypeColor(type?: string) {
  if (type === "Asset") return "green";
  if (type === "Liability") return "red";
  if (type === "Equity") return "blue";
  if (type === "Income") return "cyan";
  if (type === "Expense") return "gold";
  return "default";
}
